import Loading from '@/components/Loading/Loading';
import { _isNotBlank, _notNil } from '@/littledash';
import type { AgentSessionApiId, AgentTreeNodeApiId } from '@/model/Agent.model';
import { useApiHook } from '@/support/Hooks/api/useApiHook';
import { setSanitizedHtml } from '@/utils/browser.utils';
import { ModalActions, ModalContainer, ModalHeader } from '@/utils/modal';
import { type FC, useEffect, useRef } from 'react';
import styles from './AgentReport.module.scss';

interface AgentReportProps {
  /** The session the report node belongs to. */
  sessionApiId: AgentSessionApiId;
  /** The report-variant tree node whose report to fetch and render. */
  nodeApiId: AgentTreeNodeApiId;
  /** Injected by the modal system; closes the modal. */
  closeModal: () => void;
}

/**
 * Modal that fetches and renders an agent result report. Opened from a result node's "View Report"
 * action, which supplies the node identity; the modal owns the fetch so it can open instantly with a
 * loading state (reports are unbounded LLM output and can be slow) and so a repeated click just
 * re-opens the modal rather than firing duplicate requests. Shows a spinner while loading, an inline
 * error message on failure, and the report otherwise.
 *
 * SECURITY: the report is LLM-generated and thus untrusted. It is sanitised server-side (jsoup) before
 * it is sent, and sanitised again here via {@link setSanitizedHtml} (DOMPurify + the native `setHTML`
 * Sanitizer API, never an innerHTML string sink) — belt-and-braces, so a gap on either side cannot
 * become live XSS.
 */
const AgentReport: FC<AgentReportProps> = ({ sessionApiId, nodeApiId, closeModal }) => {
  const { response, loading } = useApiHook({
    endpoint: 'GET /api/v1/ai-agents/sessions/{sessionApiId}/nodes/{nodeApiId}/report',
    path: { sessionApiId, nodeApiId },
    options: {
      onError: {
        throw: false,
        preserveOriginalError: false,
        toast: false,
        capture: true,
        slug: 'agent-node-report-load',
      },
    },
  });

  const html = response?.type === 'success' ? response.body.html : '';
  const bodyRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (_isNotBlank(html) && _notNil(bodyRef.current)) {
      setSanitizedHtml(bodyRef.current, html);
    }
  }, [html]);

  if (loading) {
    return <Loading txt="Loading report" />;
  }

  return (
    <ModalContainer size="medium" testKey="agent-report-modal">
      <ModalHeader title="Report" closeModal={closeModal} className="pa3 bb b--moon-gray bg-white" />
      {response?.type === 'error' ? (
        <div className={styles.body} data-testid="agent-report-content">
          <p className="gray tc" data-test-element="error">
            Couldn't load the report.
          </p>
        </div>
      ) : _isNotBlank(html) ? (
        <div ref={bodyRef} className={styles.body} data-testid="agent-report-content" />
      ) : (
        <div className={styles.body} data-testid="agent-report-content">
          <p className="gray tc" data-test-element="empty-state">
            No report content.
          </p>
        </div>
      )}
      <div className="pa3 bt b--moon-gray">
        <ModalActions onCancel={closeModal} cancelBtnText="Close" />
      </div>
    </ModalContainer>
  );
};

export default AgentReport;
